/**
 * CR-6347 / CR-6348: shared dependent-filter cascade logic for the reworked
 * PO/SO Lines filter panels.
 *
 * Extracted from SupplierSOLinesFilters so the supplier and customer sectioned
 * panels drive identical cascade behaviour (Country -> Site, Division -> Site,
 * Site -> Warehouse, Supplier Group -> Supplier Site) without duplicating it.
 *
 * The customer panel passes a `customersOverride` (its view-scoped company list)
 * so hierarchy scoping is preserved; the supplier panel omits it and uses the
 * backend-scoped `filterOptions.customers` as-is.
 */
import { useMemo } from 'react';
import { POLinesFilters, FilterOptions } from '../types';

export function usePOLinesFilterCascades(
  filters: POLinesFilters,
  filterOptions: FilterOptions | null,
  onFilterChange: (key: string, value: string) => void,
  customersOverride?: FilterOptions['customers'],
) {
  // Full backend list — used for validation lookups so a currently-selected
  // customer (e.g. from a URL param) is always resolvable even when the visible
  // options are view-scoped down below.
  const allCustomers = filterOptions?.customers ?? [];
  // Customer view passes a view-scoped list to narrow the *visible* options;
  // supplier view omits it and uses the (already backend-scoped) full list.
  const customersForOptions = customersOverride ?? allCustomers;
  const suppliers = filterOptions?.suppliers ?? [];
  const warehouses = filterOptions?.warehouses ?? [];

  // Company Site: narrowed by the selected Country and Division. The currently
  // selected customer is always kept in the list (resolved from the full list)
  // so its label still renders — and can be cleared — even when view-scoping or
  // the country/division filters would otherwise exclude it.
  const companySiteOptions = useMemo(() => {
    const scoped = customersForOptions.filter(
      (c) =>
        (!filters.companyCountry || c.country === filters.companyCountry) &&
        (!filters.division || String(c.group ?? '') === filters.division),
    );
    if (filters.customer && !scoped.some((c) => String(c.id) === filters.customer)) {
      const sel = allCustomers.find((c) => String(c.id) === filters.customer);
      if (sel) return [sel, ...scoped];
    }
    return scoped;
  }, [customersForOptions, allCustomers, filters.customer, filters.companyCountry, filters.division]);

  // Warehouse: narrowed to the selected Company Site (warehouse.site === site id).
  const warehouseOptions = useMemo(
    () => warehouses.filter((w) => !filters.customer || String(w.site) === filters.customer),
    [warehouses, filters.customer],
  );

  // Supplier Site: narrowed by the selected Supplier Country and Supplier Group.
  // CR-6347: Added supplierCountry cascade to match client feedback requirements.
  const supplierSiteOptions = useMemo(
    () =>
      suppliers.filter(
        (s) =>
          (!filters.supplierCountry || s.country === filters.supplierCountry) &&
          (!filters.supplierGroup || String(s.group ?? '') === filters.supplierGroup),
      ),
    [suppliers, filters.supplierCountry, filters.supplierGroup],
  );

  // --- Cascade change handlers (clear now-invalid dependents) -------------
  const handleCompanyCountryChange = (value: string) => {
    onFilterChange('companyCountry', value);
    if (value && filters.customer) {
      const sel = allCustomers.find((c) => String(c.id) === filters.customer);
      if (sel && sel.country !== value) {
        onFilterChange('customer', '');
        onFilterChange('warehouse', '');
      }
    }
  };

  const handleDivisionChange = (value: string) => {
    onFilterChange('division', value);
    if (value && filters.customer) {
      const sel = allCustomers.find((c) => String(c.id) === filters.customer);
      if (sel && String(sel.group ?? '') !== value) {
        onFilterChange('customer', '');
        onFilterChange('warehouse', '');
      }
    }
  };

  const handleCompanySiteChange = (value: string) => {
    onFilterChange('customer', value);
    // Warehouse depends on the site — drop it whenever it can't be confirmed to
    // belong to the new site: site cleared, warehouse no longer in the option
    // list (stale/URL value), or it belongs to a different site.
    if (filters.warehouse) {
      const wh = warehouses.find((w) => String(w.id) === filters.warehouse);
      if (!value || !wh || String(wh.site) !== value) {
        onFilterChange('warehouse', '');
      }
    }
  };

  const handleSupplierGroupChange = (value: string) => {
    onFilterChange('supplierGroup', value);
    if (value && filters.supplier) {
      const sel = suppliers.find((s) => String(s.id) === filters.supplier);
      if (sel && String(sel.group ?? '') !== value) {
        onFilterChange('supplier', '');
      }
    }
  };

  // CR-6347: Supplier Country cascade — clears supplier site if it doesn't match
  // the newly selected country.
  const handleSupplierCountryChange = (value: string) => {
    onFilterChange('supplierCountry', value);
    if (value && filters.supplier) {
      const sel = suppliers.find((s) => String(s.id) === filters.supplier);
      if (sel && sel.country !== value) {
        onFilterChange('supplier', '');
      }
    }
  };

  return {
    companySiteOptions,
    warehouseOptions,
    supplierSiteOptions,
    handleCompanyCountryChange,
    handleDivisionChange,
    handleCompanySiteChange,
    handleSupplierGroupChange,
    handleSupplierCountryChange,
  };
}
